import React, { type FC, type PropsWithChildren } from 'react'
import { Box, Table as MuiTable, TableBody as MuiTableBody, type SxProps, TableContainer, type Theme } from '@mui/material'

import { isArrayNotEmpty } from '../../../../utils'

import { extractDataTestId } from '../../utils'
import { EmptyTableBody } from './Empty'
import { ErrorTableBody } from './Error'
import { Footer } from './Footer'
import { Header } from './Header'
import { Row } from './Row'
import { ReloadingBar, SkeletonTableBody } from './Skeleton'
import { PropsProvider, useBodyState, useHeight, useVisibleRows } from './State'
import { Toolbar } from './Toolbar'
import type { Line, Props } from './Types'
import { ColumnGroup, useTableLayout } from './Widths'

const containerWrapperStyle: SxProps<Theme> = { position: 'relative' }
/** The frame draws the border and doesn't scroll, so the pager below the rows stays outside the scroll area and its scrollbar */
const frameStyle: SxProps<Theme> = { overflow: 'hidden' }
const scrollAreaStyle: SxProps<Theme> = { overflow: 'auto' }

const ScrollArea: FC<PropsWithChildren> = ({ children }) => {
    const maxHeight = useHeight()

    return (
        <Box data-testid="table-scroll-area" sx={[scrollAreaStyle, { maxHeight }]}>
            {children}
        </Box>
    )
}

const LoadedBodyContent: FC = () => {
    const visibleRows = useVisibleRows()

    if (!isArrayNotEmpty(visibleRows)) {
        return <EmptyTableBody />
    }

    return (
        <>
            {visibleRows.map((row, rowIndex) => <Row key={rowIndex} index={rowIndex} row={row} />)}
        </>
    )
}

const BodyContent: FC = () => {
    switch (useBodyState()) {
        case 'error':
            return <ErrorTableBody />
        case 'rows':
            return <LoadedBodyContent />
        case 'skeleton':
            return <SkeletonTableBody />
        case 'reloading':
            return <LoadedBodyContent />
    }
}

const ReloadingOverlay: FC = () => (useBodyState() === 'reloading' ? <ReloadingBar /> : null)

const BusyTable: FC<PropsWithChildren> = ({ children }) => {
    const bodyState = useBodyState()
    const busy = bodyState === 'skeleton' || bodyState === 'reloading'
    const layout = useTableLayout()

    return (
        <MuiTable stickyHeader size="medium" aria-busy={busy || undefined} sx={layout?.sx} style={layout?.style}>
            <ColumnGroup />
            {children}
        </MuiTable>
    )
}

export const Table = <L extends Line>({ children, ...props }: PropsWithChildren<Props<L>>): ReturnType<FC> => (
    <PropsProvider value={props}>
        <Box data-testid={extractDataTestId(props) || 'common-table'}>
            <Toolbar />
            <Box sx={containerWrapperStyle}>
                <TableContainer sx={frameStyle}>
                    <ScrollArea>
                        <BusyTable>
                            <Header />
                            <MuiTableBody>
                                <BodyContent />
                            </MuiTableBody>
                        </BusyTable>
                    </ScrollArea>
                    <Footer />
                </TableContainer>
                <ReloadingOverlay />
            </Box>
            {children}
        </Box>
    </PropsProvider>
)
